import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import { postsByDate } from '../blog/posts';

const SITE_URL = 'https://qcoda.com';

// Blog index: renders the most recent post in full, with previous posts
// as compact cards below. Emits Blog + BlogPosting JSON-LD so answer engines
// can surface individual articles from this hub page.
function Blog() {
    const featured = postsByDate[0] || null;
    const previous = postsByDate.slice(1);

    const blogJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Qcoda Blog',
        description:
            'Field notes on AI Engineering Governance — keeping AI-assisted software development private, aligned, and on the record.',
        url: `${SITE_URL}/blog`,
        publisher: { '@type': 'Organization', name: 'Qcoda', url: `${SITE_URL}/` },
        blogPost: postsByDate.map((p) => ({
            '@type': 'BlogPosting',
            headline: p.title,
            description: p.description,
            datePublished: p.date,
            url: `${SITE_URL}/blog/${p.slug}`,
            author: { '@type': 'Person', name: p.author },
        })),
    };

    return (
        <main>
            <Seo
                title="Blog — Qcoda | AI Engineering Governance"
                description="Field notes on AI Engineering Governance — keeping AI-assisted software development private, aligned, and provable on ground you own."
                path="/blog"
                jsonLd={blogJsonLd}
                jsonLdId="blog-jsonld"
            />

            {/* ===== FEATURED POST (most recent, rendered in full) ===== */}
            {featured && (
                <article className="site-section-lg">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg={9} xl={8}>

                                {/* Header */}
                                <header className="blog-post-header">
                                    <h1 className="blog-post-title">{featured.title}</h1>
                                    <p className="blog-post-dek">{featured.subtitle}</p>
                                    <div className="blog-post-byline">
                                        <span className="blog-post-author">{featured.author}</span>
                                        {featured.authorTitle && (
                                            <span className="blog-post-author-title">, {featured.authorTitle}</span>
                                        )}
                                        <span aria-hidden="true"> · </span>
                                        <time dateTime={featured.date}>{featured.dateDisplay}</time>
                                        <span aria-hidden="true"> · </span>
                                        <span>{featured.readingTime}</span>
                                    </div>
                                </header>

                                {/* Hero image */}
                                {featured.heroImage && (
                                    <figure className="blog-post-hero">
                                        <img
                                            src={featured.heroImage}
                                            alt={featured.heroAlt || featured.title}
                                        />
                                    </figure>
                                )}

                                {/* Full body */}
                                <div
                                    className="blog-post-body"
                                    dangerouslySetInnerHTML={{ __html: featured.bodyHtml }}
                                />

                                {/* Footer CTA */}
                                <footer className="blog-post-footer">
                                    <a href="/#waitlist" className="btn-primary-cta blog-post-cta">
                                        Ask about early access
                                    </a>
                                </footer>

                            </Col>
                        </Row>
                    </Container>
                </article>
            )}

            {/* ===== PREVIOUS ESSAYS ===== */}
            {previous.length > 0 && (
                <section className="site-section" style={{ paddingTop: 0 }}>
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg={9} xl={8}>
                                <hr className="section-divider" />
                                <p
                                    style={{
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: 'rgba(255,255,255,0.35)',
                                        marginBottom: '1.5rem',
                                    }}
                                >
                                    Previous essays
                                </p>
                            </Col>
                        </Row>
                        <Row className="justify-content-center g-4">
                            {previous.map((post) => (
                                <Col lg={9} xl={8} key={post.slug}>
                                    <Link to={`/blog/${post.slug}`} className="postcard-link">
                                        <article
                                            className="postcard"
                                            style={{ display: 'flex', flexDirection: 'row', gap: '1.25rem' }}
                                        >
                                            {post.heroImage && (
                                                <div
                                                    className="postcard-media"
                                                    style={{
                                                        flexShrink: 0,
                                                        width: '120px',
                                                        height: '80px',
                                                        borderRadius: '6px',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <img
                                                        src={post.heroImage}
                                                        alt={post.heroAlt || post.title}
                                                        loading="lazy"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </div>
                                            )}
                                            <div className="postcard-body" style={{ padding: 0 }}>
                                                <div className="postcard-meta">
                                                    <time dateTime={post.date}>{post.dateDisplay}</time>
                                                    <span aria-hidden="true"> · </span>
                                                    <span>{post.readingTime}</span>
                                                </div>
                                                <h2 className="postcard-title" style={{ fontSize: '1rem' }}>{post.title}</h2>
                                                <p className="postcard-dek" style={{ fontSize: '0.875rem' }}>{post.subtitle}</p>
                                                <span className="postcard-cta">Read the essay →</span>
                                            </div>
                                        </article>
                                    </Link>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            )}
        </main>
    );
}

export default Blog;
